import { hubConnectorSettings } from 'settings/hubConnectorSettings'

export enum FailedRequestReason {
  OFFLINE = 'OFFLINE',
  TIMEOUT = 'TIMEOUT',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export class OfflineRequestError extends Error {
  constructor(reason: FailedRequestReason) {
    super(reason)

    this.name = 'OfflineRequestError'
  }
}

export class ApiResponseError extends Error {
  statusCode: number
  retryable: boolean

  constructor(message: string, statusCode: number, retryable: boolean) {
    super(message)

    this.statusCode = statusCode

    this.retryable = retryable
  }
}

// Cloud Run's own errors and the API's 404 for an unknown route aren't JSON
const readErrorBody = (res: Response) => res.json().catch(() => null)

export const errorCodes = async (res: Response) => {
  const { status } = res

  let error

  switch (true) {
    case status === 204:
      return

    case status >= 200 && status <= 399:
      return await res.json()

    case status === 401: {
      await hubConnectorSettings.onApiFetchStatus401(res)

      error = await readErrorBody(res)

      // banyan-api's auth wrappers send every 401 as { errorMessage }
      throw new ApiResponseError(error?.errorMessage, status, true)
    }

    case status === 404 || status === 403: {
      const error = await readErrorBody(res)
      const message = error?.message || error?.error || (status === 404 ? 'Not found' : 'Forbidden')

      throw new ApiResponseError(message, status, false)
    }

    case status === 409: {
      error = await readErrorBody(res)

      throw new ApiResponseError(error?.message, status, false)
    }

    case status === 429: {
      error = await readErrorBody(res)

      const message = error?.message || 'Too Many Requests. Try Again Later'
      const retryable = error?.retryable ?? true

      throw new ApiResponseError(message, status, retryable)
    }

    case status >= 400 && status < 500: {
      error = await readErrorBody(res)

      const message = error?.message || 'Bad request'
      const retryable = error?.retryable ?? false

      throw new ApiResponseError(message, status, retryable)
    }

    case status >= 500: {
      error = await readErrorBody(res)

      const message = error?.message || 'Server Error. Try Again Later'
      // The API answers its own 500s in JSON, so a 500 without a JSON body came from Cloud Run
      const isCloudRun500 = status === 500 && error === null
      const retryable = error?.retryable ?? (isCloudRun500 || [502, 503, 504].includes(status))

      throw new ApiResponseError(message, status, retryable)
    }

    default:
      // eslint-disable-next-line no-console
      console.log('Unknown error', res)

      throw new ApiResponseError('Network Error. Try Again Later', status, true)
  }
}
